import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import Calendar from "./Calendar";
import { cn } from "../../lib/utils";
import {
  clampIsoDateToMax,
  clampIsoDateToMin,
  formatIsoDateToDisplay,
  getCurrentMonthRange,
  getCurrentBusinessMonthRange,
  getLastThreeMonthsRange,
  getPreviousMonthRange,
  getTodayRange,
  parseDisplayDateToIso,
} from "../../utils/dateFilters";

function emitDateChange(handler, name, value) {
  if (typeof handler !== "function") return;
  handler({
    target: {
      name,
      value,
    },
  });
}

function normalizeIsoDate(value) {
  return parseDisplayDateToIso(value) || "";
}

function constrainIsoDate(value, minDate, maxDate) {
  const iso = normalizeIsoDate(value);
  if (!iso) return "";
  let constrained = iso;
  const normalizedMinDate = normalizeIsoDate(minDate);
  const normalizedMaxDate = normalizeIsoDate(maxDate);
  if (normalizedMinDate) {
    constrained = clampIsoDateToMin(constrained, normalizedMinDate);
  }
  if (normalizedMaxDate) {
    constrained = clampIsoDateToMax(constrained, normalizedMaxDate);
  }
  return constrained;
}

function dateToIsoString(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoToLocalDate(iso) {
  if (!iso) return undefined;
  const match = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

const PRESETS = [
  { label: "Hoje", range: getTodayRange },
  { label: "Mês atual", range: getCurrentMonthRange },
  { label: "Mês anterior", range: getPreviousMonthRange },
  { label: "Mês útil", range: getCurrentBusinessMonthRange },
  { label: "Últimos 3 meses", range: getLastThreeMonthsRange },
];

const PRESET_BUTTON =
  "inline-flex h-7 items-center rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground transition " +
  "hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function DateRangeField({
  label = "Período",
  from,
  to,
  onFromChange,
  onToChange,
  fromName = "from",
  toName = "to",
  className = "",
  showPresets = true,
  minDate = "",
  maxDate = "",
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const fromValue = constrainIsoDate(from, minDate, maxDate);
  const toValue = constrainIsoDate(to, minDate, maxDate);

  // Padrão do sistema: período limpo = mês atual, limitado a minDate/maxDate. Período vazio não é
  // opção — as consultas que exigem período (ex.: dashboard) quebravam ao limpar.
  const defaultRange = useMemo(() => {
    const month = getCurrentMonthRange();
    return {
      from: constrainIsoDate(month.from, minDate, maxDate),
      to: constrainIsoDate(month.to, minDate, maxDate),
    };
  }, [minDate, maxDate]);
  const isDefaultRange = fromValue === defaultRange.from && toValue === defaultRange.to;

  const displayRangeText = useMemo(() => {
    if (fromValue && toValue) {
      if (fromValue === toValue) return `Em ${formatIsoDateToDisplay(fromValue)}`;
      return `Entre ${formatIsoDateToDisplay(fromValue)} e ${formatIsoDateToDisplay(toValue)}`;
    }
    if (fromValue) return `A partir de ${formatIsoDateToDisplay(fromValue)}`;
    if (toValue) return `Até ${formatIsoDateToDisplay(toValue)}`;
    return "Selecione o período";
  }, [fromValue, toValue]);

  const daysCount = useMemo(() => {
    const start = parseIsoToLocalDate(fromValue);
    const end = parseIsoToLocalDate(toValue);
    if (!start || !end) return 0;
    return Math.round(Math.abs(end.getTime() - start.getTime()) / 86400000) + 1;
  }, [fromValue, toValue]);

  // Rascunho enquanto o calendário está aberto. A tela só recebe o período quando ele fica completo
  // (início e fim diferentes) ou no "Confirmar". Emitir já no primeiro clique disparava uma busca
  // com período de um dia antes de a pessoa escolher o fim.
  const [draft, setDraft] = useState({ from: undefined, to: undefined });

  const disabledMatcher = useMemo(() => {
    const matchers = [];
    const min = parseIsoToLocalDate(normalizeIsoDate(minDate));
    const max = parseIsoToLocalDate(normalizeIsoDate(maxDate));
    if (min) matchers.push({ before: min });
    if (max) matchers.push({ after: max });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const emitRange = (fromIso, toIso) => {
    emitDateChange(onFromChange, fromName, constrainIsoDate(fromIso, minDate, maxDate));
    emitDateChange(onToChange, toName, constrainIsoDate(toIso, minDate, maxDate));
  };

  const openCalendar = () => {
    setDraft({ from: parseIsoToLocalDate(fromValue), to: parseIsoToLocalDate(toValue) });
    setIsOpen(true);
  };

  const applyPreset = (range) => {
    emitRange(range.from, range.to);
    setIsOpen(false);
  };

  const clearRange = () => {
    emitRange(defaultRange.from, defaultRange.to);
    setIsOpen(false);
  };

  const handleSelect = (range) => {
    const next = range || { from: undefined, to: undefined };
    setDraft(next);
    const fromIso = dateToIsoString(next.from);
    const toIso = dateToIsoString(next.to);
    if (fromIso && toIso && fromIso !== toIso) {
      emitRange(fromIso, toIso);
    }
  };

  const confirmDraft = () => {
    const fromIso = dateToIsoString(draft?.from);
    if (fromIso) {
      emitRange(fromIso, dateToIsoString(draft?.to) || fromIso);
    }
    setIsOpen(false);
  };

  const draftText = draft?.from
    ? `${formatIsoDateToDisplay(dateToIsoString(draft.from))} – ${
        draft.to ? formatIsoDateToDisplay(dateToIsoString(draft.to)) : "escolha o fim"
      }`
    : "Escolha o início";

  return (
    <div className={cn("relative space-y-1.5", className)} ref={containerRef}>
      {label && <label className="text-sm font-medium text-foreground">{label}</label>}

      <div className="relative">
        <button
          type="button"
          onClick={() => (isOpen ? setIsOpen(false) : openCalendar())}
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-lg border border-input bg-background pl-3 text-left text-sm text-foreground transition-colors",
            "hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            isDefaultRange ? "pr-9" : "pr-16",
            isOpen && "border-foreground/40 ring-2 ring-ring/25"
          )}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="truncate font-semibold">{displayRangeText}</span>
          {daysCount > 0 && (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              {daysCount} {daysCount === 1 ? "dia" : "dias"}
            </span>
          )}
        </button>

        {/* Controles à direita como irmãos do gatilho — não aninhados dentro dele (botão dentro de
            botão é HTML inválido e confunde a navegação por teclado). */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {!isDefaultRange && (
            <button
              type="button"
              onClick={clearRange}
              title="Voltar ao mês atual"
              aria-label="Voltar ao mês atual"
              className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown
            className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")}
            aria-hidden="true"
          />
        </div>
      </div>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Escolher período"
          className="absolute left-0 top-full z-[150] mt-1.5 w-full rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-2xl sm:w-[336px]"
        >
          {showPresets && (
            <div className="mb-2 flex flex-wrap gap-1.5 border-b border-border pb-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => applyPreset(preset.range())}
                  className={PRESET_BUTTON}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          <Calendar
            mode="range"
            selected={draft}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            defaultMonth={draft?.from || new Date()}
            className="mx-auto"
          />

          <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
            <span className="truncate text-[11px] text-muted-foreground tabular-nums">{draftText}</span>
            <div className="flex shrink-0 items-center gap-1.5">
              {!isDefaultRange && (
                <button
                  type="button"
                  onClick={clearRange}
                  className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3 w-3" />
                  Limpar
                </button>
              )}
              <button
                type="button"
                onClick={confirmDraft}
                className="inline-flex h-7 items-center rounded-md bg-action px-3 text-[11px] font-bold text-action-foreground shadow-sm transition hover:bg-action/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
